/**
 * In-app viewer for a registration's documents (CI photos, voucher, delegation letter…).
 * Shown next to the registration data so an admin can compare what was typed with what
 * is in the photo — no new tabs. Images: zoom, drag to pan, rotate 90° (ID photos often
 * arrive sideways), open in tab, download. PDFs: embedded.
 * Keyboard (click the viewer first): ← / → switch document, + / − zoom, R rotate, 0 fit.
 *
 * Files are private: each view asks the event-doc-upload function for a 5-minute signed URL.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, ExternalLink, FileX, Loader2, Maximize2, RotateCcw, RotateCw, ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getRegistrationDocumentUrl } from '../public/publicRegistrationRepository';
import type { DocumentKind } from '../public/types';

export interface PanelDoc {
  kind: DocumentKind;
  label: string;
  /** Storage key; undefined = not uploaded yet */
  key?: string;
}

interface RegistrationDocumentPanelProps {
  docs: PanelDoc[];
  selectedKind: DocumentKind | null;
  onSelect: (kind: DocumentKind) => void;
  className?: string;
}

const ZOOM_STEPS = [0.5, 0.75, 1, 1.5, 2, 3, 4];
const PADDING = 16;

export function RegistrationDocumentPanel({ docs, selectedKind, onSelect, className = '' }: RegistrationDocumentPanelProps) {
  const available = docs.filter((d): d is PanelDoc & { key: string } => !!d.key);
  const current = available.find((d) => d.kind === selectedKind) ?? available[0];
  const isPdf = !!current && current.key.toLowerCase().endsWith('.pdf');

  const {
    data: url,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['registration_doc_url', current?.key],
    queryFn: () => getRegistrationDocumentUrl(current!.key),
    enabled: !!current,
    staleTime: 4 * 60 * 1000, // signed URLs last 5 minutes
    gcTime: 5 * 60 * 1000,
    retry: 1,
  });

  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [imageFailed, setImageFailed] = useState(false);
  const retriedUrl = useRef(false);

  // Each document starts fitted and unrotated
  useEffect(() => {
    setRotation(0);
    setZoom(1);
    setNatural(null);
    setImageFailed(false);
    retriedUrl.current = false;
  }, [current?.key]);

  // Size of the viewing area → "fit" scale
  const [boxEl, setBoxEl] = useState<HTMLDivElement | null>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  useEffect(() => {
    if (!boxEl) return;
    const observer = new ResizeObserver(([entry]) =>
      setBox({ w: entry.contentRect.width, h: entry.contentRect.height }),
    );
    observer.observe(boxEl);
    return () => observer.disconnect();
  }, [boxEl]);

  const quarterTurn = rotation % 180 !== 0;
  const rotatedW = natural ? (quarterTurn ? natural.h : natural.w) : 0;
  const rotatedH = natural ? (quarterTurn ? natural.w : natural.h) : 0;
  const fit =
    natural && box.w > 0 && box.h > 0
      ? Math.min((box.w - PADDING) / rotatedW, (box.h - PADDING) / rotatedH, 2)
      : 1;
  const scale = fit * zoom;

  const zoomIn = useCallback(() => setZoom((z) => ZOOM_STEPS.find((s) => s > z + 0.001) ?? z), []);
  const zoomOut = useCallback(
    () => setZoom((z) => [...ZOOM_STEPS].reverse().find((s) => s < z - 0.001) ?? z),
    [],
  );
  const rotate = useCallback((delta: number) => setRotation((r) => (r + delta + 360) % 360), []);

  const move = (delta: number) => {
    if (available.length < 2 || !current) return;
    const index = available.findIndex((d) => d.kind === current.kind);
    const next = available[(index + delta + available.length) % available.length];
    onSelect(next.kind);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const handlers: Record<string, () => void> = {
      ArrowLeft: () => move(-1),
      ArrowRight: () => move(1),
      '+': zoomIn,
      '=': zoomIn,
      '-': zoomOut,
      r: () => rotate(90),
      R: () => rotate(90),
      '0': () => setZoom(1),
    };
    const handler = handlers[e.key];
    if (handler && !isPdf) {
      e.preventDefault();
      handler();
    } else if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && isPdf) {
      e.preventDefault();
      move(e.key === 'ArrowLeft' ? -1 : 1);
    }
  };

  // Drag to pan when zoomed in
  const drag = useRef<{ x: number; y: number; left: number; top: number } | null>(null);
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (zoom <= 1) return;
    const el = e.currentTarget;
    drag.current = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop };
    el.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    e.currentTarget.scrollLeft = drag.current.left - (e.clientX - drag.current.x);
    e.currentTarget.scrollTop = drag.current.top - (e.clientY - drag.current.y);
  };
  const endDrag = () => {
    drag.current = null;
  };

  const download = async () => {
    if (!url || !current) return;
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error('download failed');
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${current.label}.${current.key.split('.').pop() ?? 'jpg'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch {
      window.open(url, '_blank', 'noopener'); // CORS or network: let the browser handle it
    }
  };

  const handleImageError = () => {
    // The signed URL may have expired while the dialog stayed open: get a fresh one once
    if (!retriedUrl.current) {
      retriedUrl.current = true;
      void refetch();
      return;
    }
    setImageFailed(true);
  };

  const iconButton = (label: string, onClick: () => void, icon: React.ReactNode, disabled = false) => (
    <Button type="button" variant="ghost" size="icon" className="size-8" title={label} aria-label={label} onClick={onClick} disabled={disabled}>
      {icon}
    </Button>
  );

  return (
    <div
      className={`flex min-h-0 flex-col overflow-hidden rounded-lg border bg-card outline-none focus-visible:ring-2 focus-visible:ring-ring ${className}`}
      tabIndex={0}
      onKeyDown={onKeyDown}
    >
      {/* Document selector */}
      <div className="flex gap-1 overflow-x-auto border-b px-2 pt-2" role="tablist">
        {docs.map((d) => {
          const active = current?.kind === d.kind;
          return (
            <button
              key={d.kind}
              type="button"
              role="tab"
              aria-selected={active}
              disabled={!d.key}
              title={d.key ? undefined : 'No cargado'}
              onClick={() => onSelect(d.kind)}
              className={`-mb-px shrink-0 border-b-2 px-3 py-1.5 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                active
                  ? 'border-primary font-semibold text-foreground'
                  : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground'
              }`}
            >
              {d.label}
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      {current && (
        <div className="flex flex-wrap items-center justify-between gap-1 border-b px-2 py-1">
          <div className="flex items-center">
            {!isPdf && (
              <>
                {iconButton('Alejar (−)', zoomOut, <ZoomOut className="size-4" />, zoom <= ZOOM_STEPS[0])}
                <span className="w-12 text-center text-xs tabular-nums text-muted-foreground">{Math.round(zoom * 100)}%</span>
                {iconButton('Acercar (+)', zoomIn, <ZoomIn className="size-4" />, zoom >= ZOOM_STEPS[ZOOM_STEPS.length - 1])}
                {iconButton('Ajustar (0)', () => setZoom(1), <Maximize2 className="size-4" />)}
                {iconButton('Girar a la izquierda', () => rotate(-90), <RotateCcw className="size-4" />)}
                {iconButton('Girar a la derecha (R)', () => rotate(90), <RotateCw className="size-4" />)}
              </>
            )}
          </div>
          <div className="flex items-center">
            {iconButton('Descargar', download, <Download className="size-4" />, !url)}
            <Button type="button" variant="ghost" size="icon" className="size-8" title="Abrir en otra pestaña" asChild disabled={!url}>
              <a href={url} target="_blank" rel="noreferrer" aria-label="Abrir en otra pestaña">
                <ExternalLink className="size-4" />
              </a>
            </Button>
          </div>
        </div>
      )}

      {/* Viewing area */}
      <div className="relative min-h-0 flex-1">
        {!current ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-sm text-muted-foreground">
            <FileX className="size-8" />
            Sin documentos para mostrar.
          </div>
        ) : isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          </div>
        ) : error || !url || imageFailed ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-sm text-muted-foreground">
            <FileX className="size-8" />
            No se pudo cargar el documento.
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setImageFailed(false);
                retriedUrl.current = false;
                void refetch();
              }}
            >
              Reintentar
            </Button>
          </div>
        ) : isPdf ? (
          <iframe key={current.key} src={url} title={current.label} className="size-full bg-white" />
        ) : (
          <div
            ref={setBoxEl}
            className={`absolute inset-0 flex overflow-auto bg-muted/40 ${zoom > 1 ? 'cursor-grab active:cursor-grabbing' : ''}`}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onDoubleClick={() => setZoom((z) => (z > 1 ? 1 : 2))}
          >
            <div
              className="relative m-auto shrink-0"
              style={natural ? { width: rotatedW * scale + PADDING, height: rotatedH * scale + PADDING } : undefined}
            >
              <img
                key={url}
                src={url}
                alt={current.label}
                draggable={false}
                onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
                onError={handleImageError}
                className="max-w-none select-none"
                style={
                  natural
                    ? {
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        width: natural.w * scale,
                        height: natural.h * scale,
                        transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
                      }
                    : { opacity: 0, height: 1 }
                }
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
